import React, { useEffect, useMemo, useRef, useState } from "react";
import "../style.css";
import { ArrowRepeat, PlusCircle } from "react-bootstrap-icons";
import JoditEditor from "jodit-react";
import { htmlToJsx } from "html-to-jsx-transform";
import { v4 as uuidv4 } from "uuid";
const uuid = uuidv4();
// Helper for uploading the image to the server
async function uploadImageToServer(imageFile, session) {
  const formData = new FormData();
  formData.append("image", imageFile);
  // No caption for single-image upload; add if needed

  const response = await fetch(
    `https://api.edit.guru-x.de/save_image/${session}`,
    {
      method: "POST",
      body: formData,
    }
  );
  if (!response.ok) {
    throw new Error("Image upload failed");
  }
  const data = await response.json();
  // Extract filename from the server's returned component string
  const match = data.image_code.match(/imageUrl="([^"]+)"/);
  if (match) {
    return match[1];
  } else {
    throw new Error("Server did not return an image filename");
  }
}

function ImageMarkerGenerator({ openModal, session }) {
  const [imageFile, setImageFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(""); // now only the filename
  const [markers, setMarkers] = useState([]);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState(null);
  const [markerText, setMarkerText] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sizePercent, setSizePercent] = useState(100);

  // This handles file selection and immediate upload
  const handleImageUpload = async (e) => {
    setMarkers([]);
    setMarkerText("");
    setSelectedMarkerIndex(null);
    const file = e.target.files[0];
    setImageSrc("");
    setImageFile(null);
    if (!file) return;
    setUploading(true);
    try {
      const filename = await uploadImageToServer(file, session);
      setImageFile(file); // optionally save, but not used for display
      setImageSrc(filename); // just the filename
    } catch (err) {
      alert("Image upload failed: " + err.message);
    }
    setUploading(false);
  };

  const handleImageClick = (e) => {
    if (!imageSrc || !renderedSize.width || !renderedSize.height) return;
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xRatio = x / rect.width;
    const yRatio = y / rect.height;
    const text = "";
    const jsx = "";
    setMarkers([...markers, { x, y, text, jsx, xRatio, yRatio }]);
  };

  const handleMarkerClick = (index) => {
    setSelectedMarkerIndex(index);
    setIsDialogOpen(true);
  };
  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };
  const handleMarkerTextChange = (e) => {
    setMarkerText(e.target.value);
  };
  const handleSaveMarkerText = () => {
    const updatedMarkers = [...markers];
    updatedMarkers[selectedMarkerIndex].text = markerText;
    updatedMarkers[selectedMarkerIndex].jsx = htmlToJsx(markerText);
    setMarkers(updatedMarkers);
    setIsDialogOpen(false);
    setMarkerText("");
    setSelectedMarkerIndex(null);
  };
  const handleDeleteMarker = () => {
    if (selectedMarkerIndex !== null && selectedMarkerIndex >= 0) {
      const updatedMarkers = [...markers];
      updatedMarkers.splice(selectedMarkerIndex, 1);
      setMarkers(updatedMarkers);
      setIsDialogOpen(false); // Optional, if you want to close a dialog
      setMarkerText("");
      setSelectedMarkerIndex(null);
    }
  };

  const generateComponentCode = () => {
    // Only the filename now in imageSrc
    const ImageMarkerComponent =
      `<div id="${uuid}" />` +
      `<ImageMarker markers={${JSON.stringify(
        markers
      )}} imageSrc={"${imageSrc}"} sizePercent="${sizePercent}%"/>`;
    setResult(ImageMarkerComponent);
    openModal && openModal(ImageMarkerComponent);
    console.log(ImageMarkerComponent);
  };

  const imageRef = useRef(null);
  const handleOnload = (e) => {
    if (imageRef.current) {
      const { width, height } = imageRef.current?.getBoundingClientRect();
      setRenderedSize({ width, height });
    }
  };
  // Update size whenever image loads or resizes
  useEffect(() => {
    if (!imageRef.current) return;

    function updateSize() {
      const rect = imageRef.current?.getBoundingClientRect();
      setRenderedSize({ width: rect?.width, height: rect?.height });
    }

    updateSize(); // set size on mount/load

    // Listen for resizes
    const observer = new ResizeObserver(updateSize);
    observer.observe(imageRef.current);

    // Clean up
    return () => observer.disconnect();
  }, [sizePercent]);

  // Reset function
  const fileInputRef = useRef(null);
  const resetAll = () => {
    setImageFile(null);
    setImageSrc("");
    setMarkers([]);
    setMarkerText("");
    setSelectedMarkerIndex(null);
    setResult(null);
    // Actually clears the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const [renderedSize, setRenderedSize] = useState({ width: 0, height: 0 });
  const editorJodit = useRef(null);
  const config = useMemo(
    () => ({
      readonly: false, // all options from https://xdsoft.net/jodit/docs/,

      //  placeholder: placeholder || "Start typings...",
    })
    //  [placeholder]
  );
  return (
    <div className="grid place-items-center m-8 p-8 items-center">
      <div className="flex flex-col w-full h-full items-center">
        <div className="my-4 items-center text-center">
          <div className=" text-sm m-2">Supported formats: JPEG and PNG</div>
          <input
            id="imageInput"
            ref={fileInputRef}
            type="file"
            className="file-input file-input-bordered file-input-primary w-full max-w-xs"
            accept="image/jpeg,image/png"
            onChange={handleImageUpload}
            disabled={uploading}
          />
          <div className="my-5 flex items-center justify-center">
            <label htmlFor="range" className="mr-3">
              Size
            </label>
            <input
              id="range"
              type="range"
              min={0}
              max="100"
              value={sizePercent}
              onChange={(e) => setSizePercent(Number(e.target.value))}
              className="range range-primary range-sm mx-4 w-44"
            />
          </div>
          <div className="h-5"></div>
          {imageSrc && (
            <div
              className="relative mx-auto  w-fit  "
              style={{
                width: `${sizePercent}%`,
                //maxWidth: imageRef.current?.getBoundingClientRect().width,
              }}
            >
              <img
                ref={imageRef}
                src={`https://api.edit.guru-x.de/preview/${session}/${imageSrc}`}
                alt="Uploaded"
                className="block max-w-full  cursor-pointer"
                onClick={handleImageClick}
                onLoad={handleOnload}
              />
              {markers.map((marker, index) => (
                <React.Fragment key={index}>
                  <div>
                    <PlusCircle
                      style={{
                        left: renderedSize.width * marker.xRatio - 16,
                        top: renderedSize.height * marker.yRatio - 16,
                        position: "absolute", // ensure direct positioning on parent
                      }}
                      onClick={() => handleMarkerClick(index)}
                      className="markerElement absolute w-8 h-8 bg-blue-600 text-white rounded-full hover:border-red-500 "
                    />
                  </div>
                  {isDialogOpen && selectedMarkerIndex === index && (
                    <div
                      style={{ zIndex: "1000" }}
                      className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-gray-800 bg-opacity-75"
                    >
                      <div className="bg-white p-4 w-9/12  rounded shadow items-start justify-start text-start">
                        {/**    <input
                          type="text"
                          value={markerText}
                          onChange={handleMarkerTextChange}
                          placeholder="Enter marker text"
                          className="border border-gray-300 p-2 mb-2 w-full"
                        />*/}
                        <JoditEditor
                          ref={editorJodit}
                          value={(markers[selectedMarkerIndex] ?? {}).text}
                          config={config}
                          tabIndex={1} // tabIndex of textarea
                          onBlur={(newContent) => setMarkerText(newContent)} // preferred to use only this option to update the content for performance reasons
                          onChange={(newContent) => {}}
                        />
                        {/**    <div>
                          Current Text:
                          {(markers[selectedMarkerIndex] ?? {}).text}
                        </div>*/}{" "}
                        <div className="flex justify-end mt-3 gap-3 ">
                          {" "}
                          <button
                            onClick={handleSaveMarkerText}
                            className=" btn btn-primary"
                          >
                            Save
                          </button>{" "}
                          <button onClick={handleDialogClose} className="btn">
                            Cancel
                          </button>
                          <button
                            onClick={handleDeleteMarker}
                            className="btn btn-error  "
                          >
                            Delete Marker
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-row m-auto pt-10">
          {" "}
          <button
            className="btn btn-square min-w-40 mx-4"
            onClick={generateComponentCode}
            disabled={!imageSrc}
          >
            Create
          </button>
          <button
            id="resetButton"
            key="resetButton"
            type="reset"
            onClick={resetAll}
            className="btn btn-circle m-auto"
          >
            <ArrowRepeat size={30} />
          </button>{" "}
        </div>
        {/**    {result && (
          <div className="mt-5 font-mono whitespace-pre">{result}</div>
        )}*/}
      </div>
    </div>
  );
}

export default ImageMarkerGenerator;
